import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Client, ContractorTypeSearchOptions, MapService } from 'src/app/models';
import { ClientService, ContractorService, SystemService, WorkflowContextService, WorkflowService } from 'src/app/services';
import { ModalConfirmComponent } from '../../system/modal-confirm/modal-confirm.component';

@Component({
  selector: 'wm-workflow-test-as-client',
  templateUrl: './workflow-test-as-client.component.html',
  styleUrls: ['./workflow-test-as-client.component.css'],
  standalone: false
})
export class WorkflowTestAsClientComponent implements OnInit, OnChanges {
  testAsClientData: { applicationId?: string; clientId?: string; contractorTypeId?: string, mapServiceId?: string } = {};
  testAsMapServices: MapService[] = [];
  needs: { needsParcelDataSource: boolean; needsMapService: boolean; needsContractorType: boolean; } = { needsParcelDataSource: false, needsMapService: false, needsContractorType: false };
  testAsClientForm: UntypedFormGroup;
  noParcelDataSources: boolean = false;
  @Output() onTest: EventEmitter<{ clientId?: string; contractorTypeId?: string; mapServiceId?: string }> = new EventEmitter();
  @Output() onCancel: EventEmitter<void> = new EventEmitter();
  testAsClients: Client[] = [];
  testAsContractorTypes: any[] = [];
  @Input() workflowId: string;
  @Input() versionId: string;
  @Input() clientId: string;
  @Input() applicationId?: string;
  @ViewChild('testAsClientModal', { static: false }) testAsClientModal: ModalConfirmComponent;
  showTestAsClient = false;

  constructor(
    private _fb: UntypedFormBuilder,
    public _context: WorkflowContextService,
    private _workflowSvc: WorkflowService,
    private _clientSvc: ClientService,
    private _systemSvc: SystemService,
    private _contractorSvc: ContractorService
  ) {
    this.testAsClientData = {};
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] && !changes['clientId'].isFirstChange()) {
      this.testAsClientData.clientId = this.clientId;
      if (this.testAsClientForm && this.testAsClientForm.contains('testClient')) {
        this.testAsClientForm.controls['testClient'].setValue(this.clientId);
      }
    }
  }

  ngOnInit() {
    this.testAsClientForm = this._fb.group({});
    const clientId: string = this._context.testAsData?.clientId || this.clientId || this._context.client?.id;
    if (clientId) {
      this.testAsClientData.clientId = clientId;
    }
    else {
      this.showTestAsClient = true;
    }
  }

  testAsClientModalOpened() {
    this._workflowSvc.getTestApplicationNeeds(this.workflowId, this.versionId).subscribe(needs => {
      if (this.testAsClientForm.contains('testClient')) {
        this.testAsClientForm.removeControl('testClient');
      }
      if (this.testAsClientForm.contains('testMapService')) {
        this.testAsClientForm.removeControl('testMapService');
      }
      if (this.testAsClientForm.contains('testContractorType')) {
        this.testAsClientForm.removeControl('testContractorType');
      }
      if (this.testAsClientForm.contains('testParcelDataSource')) {
        this.testAsClientForm.removeControl('testParcelDataSource');
      }
      this.testAsClientForm.addControl('testClient', new UntypedFormControl(null, Validators.required));
      this.testAsClientForm.addControl('testMapService', new UntypedFormControl(null, needs.needsMapService ? Validators.required : Validators.nullValidator));
      this.testAsClientForm.addControl('testContractorType', new UntypedFormControl(null, needs.needsContractorType ? Validators.required : Validators.nullValidator));
      this.testAsClientForm.addControl('testParcelDataSource', new UntypedFormControl(null, needs.needsParcelDataSource ? Validators.required : Validators.nullValidator));

      this.needs = needs;
      if (this.showTestAsClient && (this.testAsClients?.length === 0 || !this.testAsClients)) {
        this._clientSvc.getClients().subscribe(clients => {
          this.testAsClients = clients;
        });
      }
      else {
        this.testAsClientForm.controls['testClient'].setValue(this.testAsClientData.clientId);
        this.testAsClientSelected(this.testAsClientData.clientId);
      }
    });
  }

  cancelTest() {
    this.reset();
  }

  reset() {
    this.showTestAsClient = true;
    this.testAsClientData.contractorTypeId = null;
    this.testAsClientData.mapServiceId = null;
    this.needs = null;
    this.testAsClientData.clientId = this.clientId;
  }

  open() {
    this.reset();
    setTimeout(() => {
      this.testAsClientModal.open();
    }, 100)

  }

  testAsClientSelected(clientId: string) {
    if (clientId) {
      if (this.needs.needsParcelDataSource) {
        this._systemSvc.hasParcelDataSource(clientId).subscribe(result => {
          this.noParcelDataSources = !result;
          if (this.noParcelDataSources) {
            this.testAsClientForm.controls['testParcelDataSource'].setValue('');
          }
          else {
            this.testAsClientForm.controls['testParcelDataSource'].setValue('true');
          }
        });
      }

      if (this.needs.needsMapService) {
        this._systemSvc.getMapServicesByClient(new Client({ id: clientId })).subscribe(mapServices => {
          this.testAsMapServices = mapServices;
          if (this.testAsMapServices?.length === 1) {
            this.testAsClientData.mapServiceId = this.testAsMapServices[0].id;
          }
          if (this.testAsMapServices?.length === 0 || !this.testAsMapServices) {
            this.testAsClientData.mapServiceId = null;
          }
        });
      }

      if (this.needs.needsContractorType) {
        const searchOptions: ContractorTypeSearchOptions = new ContractorTypeSearchOptions();
        searchOptions.client = new Client({ id: clientId });

        this._contractorSvc.searchContractorTypes(searchOptions).subscribe(contractorTypes => {
          this.testAsContractorTypes = contractorTypes;
        })
      }
    }
  }

  testAsClient() {
    if (this.testAsClientForm.valid) {
      this.testAsClientData.applicationId = this.applicationId;
      this.testAsClientData.clientId = this.testAsClientForm.controls['testClient'].value;
      this.testAsClientData.mapServiceId = this.testAsClientForm.controls['testMapService'].value;
      this.testAsClientData.contractorTypeId = this.testAsClientForm.controls['testContractorType'].value;

      this._context.testAsData$.next(this.testAsClientData);
      this.onTest.emit(this.testAsClientData);
    }
  }
}
